import type { Signal } from '@preact/signals-react';
import { useSignal } from '@preact/signals-react';
import dot from 'dot-object';
import type { Ref } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  JsonDataWrapper,
  OdinDataRetrievalOptions,
  OdinGetDataFunction,
  OdinGetSubscriberFunction,
} from '../../../common/data-retrieval/data-retrieval-interfaces';
import type { OdinDataSender } from '../../../common/data-retrieval/odin-data-sender';
import useStateRef from '../../../common/hooks/use-state-ref';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import ContinuousScrollerDataLoadingIndicator from '../../continuous-scroller/continuous-scroller-data-loading-indicator.component';
import type { ContinuousScrollerFilter } from '../../continuous-scroller/continuous-scroller-types';
import DataFilter from '../../data/data-filter/data-filter.component';
import type { DropDownOptionRendererRef } from '../../data/drop-down-option-renderer/drop-down-option-renderer.component';
import DropDownOptionRenderer from '../../data/drop-down-option-renderer/drop-down-option-renderer.component';
import type { DynamicFieldComponentProps, DynamicFieldDataProps } from '../../dynamic-form/dynamic-form.interfaces';
import { DataSearchLocation } from '../../enums';
import type { JsonData, JsonDataItem } from '../../generic-types';
import { OptionNavigator } from '../../navigation/option-navigator/option-navigator.component';
import type { DropDownResult } from '../drop-down/drop-down-types';
import { DropDownSelectionType } from '../drop-down/drop-down-types';

export interface ListBoxProps extends DynamicFieldComponentProps<Array<DropDownResult>>, DynamicFieldDataProps {
  allowSearch?: boolean;
  showAddNewOption?: boolean;
  debounceTime?: number;
  itemsToRemove?: Array<DropDownResult>;
  filterLocation?: DataSearchLocation;
  onDoubleClick?: (value: DropDownResult) => void;
  dropDownDataLoaded?: (page: number, data: JsonData) => void;
  optionListStyle?: string;
  optionHoverStyle?: string;
  optionSelectedStyle?: string;
  onNewItemCreated?: (value: JsonDataItem) => void;
  preventMultiSelect?: boolean;
  subType?: string;
  scrollerRef?: Ref<DropDownOptionRendererRef>;
  filterSignal?: Signal<Array<ContinuousScrollerFilter>>;
}

const ListBox = ({
  id,
  textField,
  valueField,
  label,
  value,
  allowSearch = true,
  readOnly = false,
  required,
  data,
  itemsToRemove,
  filterLocation = DataSearchLocation.Local,
  placeholder,
  onChange,
  onDoubleClick,
  dropDownDataLoaded,
  optionListStyle,
  optionHoverStyle,
  optionSelectedStyle,
  showAddNewOption = false,
  debounceTime = 500,
  pageSize = 50,
  onNewItemCreated,
  preventMultiSelect = false,
  subType,
  scrollerRef,
  filterSignal,
}: ListBoxProps) => {
  const [scrollElement, setScrollElement] = useState<Element>();
  const [highlightedValue, setHighlightedValue] = useState<Array<DropDownResult>>();
  const [selectedValue, setSelectedValue, selectedValueRef] = useStateRef<Array<DropDownResult>>();

  const saveSender = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const currentItemData = useRef<JsonData | undefined>(undefined);
  const highlightedIndex = useRef<number | undefined>(undefined);
  const isShiftDown = useRef<boolean>(false);
  const isCtrlCmdDown = useRef<boolean>(false);
  const firstValue = useRef<DropDownResult | undefined>(undefined);

  const { t } = useTranslation();

  const _filterSignal = useSignal<Array<ContinuousScrollerFilter>>([]);

  const dataLoadIndicator = useMemo(() => <ContinuousScrollerDataLoadingIndicator />, []);

  useEffect(() => {
    setSelectedValue(value);
  }, [setSelectedValue, value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: onItemSelected is defined below, stable due to refs
  const onKeyDown = useCallback((key: string, highlightedValue?: DropDownResult) => {
    if (key === 'Enter') {
      if (highlightedValue) {
        onItemSelected(highlightedValue);
      } else if (firstValue.current) {
        onItemSelected(firstValue.current);
      }
    }
  }, []);

  const onItemSelected = (item: DropDownResult) => {
    let returnItems: Array<DropDownResult> | undefined;

    if (currentItemData.current) {
      if (isCtrlCmdDown.current && !preventMultiSelect) {
        if (selectedValueRef.current) {
          const itemIndex = selectedValueRef.current.findIndex((value) => value.value === item.value);
          if (itemIndex > -1) {
            returnItems = selectedValueRef.current.filter((value) => value.value !== item.value);
            setSelectedValue(returnItems);
          } else {
            returnItems = [...(selectedValueRef.current || []), item];
            setSelectedValue(returnItems);
          }
        } else {
          returnItems = [item];
          setSelectedValue(returnItems);
        }
      } else if (selectedValueRef.current && isShiftDown.current && !preventMultiSelect) {
        const startingItemIndex = currentItemData.current.findIndex((dataItem) =>
          selectedValueRef.current
            ? dot.pick(valueField, dataItem) === selectedValueRef.current[selectedValueRef.current.length - 1].value
            : false
        );
        const endingItemIndex = currentItemData.current.findIndex(
          (dataItem) => dot.pick(valueField, dataItem) === item.value
        );
        const selectedItems: Array<DropDownResult> =
          endingItemIndex > startingItemIndex
            ? currentItemData.current.slice(startingItemIndex, endingItemIndex + 1).map((dataItem) => {
                return {
                  value: dot.pick(valueField, dataItem),
                  text: dot.pick(textField, dataItem),
                  originalData: dataItem,
                };
              })
            : currentItemData.current.slice(endingItemIndex, startingItemIndex + 1).map((dataItem) => {
                return {
                  value: dot.pick(valueField, dataItem),
                  text: dot.pick(textField, dataItem),
                  originalData: dataItem,
                };
              });

        const filteredValues = selectedValueRef.current
          ? selectedItems.filter(
              (item) => !selectedValueRef.current?.some((selectedItem) => selectedItem.value === item.value)
            )
          : selectedItems;

        returnItems = [...(selectedValueRef.current || []), ...filteredValues];
        setSelectedValue(returnItems);
      } else {
        setSelectedValue([item]);
      }

      const selectedIndex = currentItemData.current?.findIndex(
        (dataItem) => dot.pick(valueField, dataItem) === item.value
      );
      highlightedIndex.current = selectedIndex > -1 ? selectedIndex : undefined;
    }

    if (highlightedValue && highlightedValue.length > 0) {
      setHighlightedValue(undefined);
    }

    onChange?.(returnItems || [item]);
  };

  const onModifierKeysChanged = (shiftKey: boolean, metaKey: boolean, ctrlKey: boolean) => {
    isShiftDown.current = shiftKey;
    isCtrlCmdDown.current = ctrlKey || metaKey;
  };

  const onHighlightedValueChanged = (value: DropDownResult, index: number) => {
    setHighlightedValue([value]);
    highlightedIndex.current = index;
  };

  const onScrollElementCreated = (element: HTMLElement) => {
    const convertedElement = element as Element;

    if (convertedElement) {
      setScrollElement(convertedElement);
    }
  };

  const scrollerData = useCallback<OdinGetDataFunction>(
    (scrollerSubscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if (data && !Array.isArray(data)) {
        const scrollerOptions: OdinDataRetrievalOptions = {
          ...options,
          fieldId: id,
          fieldType: 'LIST_BOX',
          pageSize,
          fieldSubType: subType,
        };

        data?.getData?.(scrollerSubscriber, scrollerOptions);
      }
    },
    [data, id, pageSize, subType]
  );

  const getScrollerSubscriber = useCallback<OdinGetSubscriberFunction>(
    (subscriber: OdinDataSender<JsonDataWrapper>) => {
      if (data && !Array.isArray(data)) {
        saveSender.current = subscriber;
        data.getSubscriber?.(subscriber, 'LIST_BOX');
      }
    },
    [data]
  );

  const pageDataLoaded = (page: number, pageData: JsonData) => {
    if (pageData.length > 0) {
      if (page === 1) {
        firstValue.current = {
          text: dot.pick(textField, pageData[0]),
          value: dot.pick(valueField, pageData[0]),
          originalData: pageData[0],
        };
      }
    }

    dropDownDataLoaded?.(page, pageData);
  };

  const itemsChanged = (itemData: JsonData) => {
    // when the available items change the first value needs to be reset
    firstValue.current = {
      text: dot.pick(textField, itemData[0]),
      value: dot.pick(valueField, itemData[0]),
      originalData: itemData[0],
    };

    currentItemData.current = itemData;
  };

  return (
    <div className="flex w-full min-w-0 flex-col">
      {label && (
        <div className="flex">
          <label>{label}</label>
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {allowSearch && (
        <DataFilter
          id={id ?? ''}
          field={textField}
          debounceTime={debounceTime}
          placeholder={placeholder ?? t('type-search')}
          filterSignal={filterSignal || _filterSignal}
          className="rounded-none border-none focus-within:outline-hidden focus-within:outline-transparent"
        />
      )}
      <OptionNavigator
        valueField={valueField}
        textField={textField}
        keyDown={onKeyDown}
        highlightedValueChanged={onHighlightedValueChanged}
        scrollElementCreated={onScrollElementCreated}
        modifierKeysChanged={onModifierKeysChanged}
        dataChanged={itemsChanged}
        pageDataLoaded={pageDataLoaded}
        change={(value) => onItemSelected(!Array.isArray(value) ? value : value[0])}
        className="w-full select-none border border-gray-4 bg-mono-1 text-sm focus:outline-primary-2"
      >
        {({ onDataChanged, onPageDataLoaded, onChange }) => (
          <DropDownOptionRenderer
            ref={scrollerRef}
            id={id}
            textField={textField}
            valueField={valueField}
            data={data}
            value={selectedValue}
            setDefaultValue={false}
            highlightedValue={highlightedValue}
            selectionType={DropDownSelectionType.Multiple}
            useCheckboxForMultiSelect={false}
            removeOnSelect={false}
            itemsToRemove={itemsToRemove}
            itemsChanged={onDataChanged}
            onChange={onChange}
            onDoubleClick={onDoubleClick}
            showAddNewOption={showAddNewOption}
            scrollElement={scrollElement}
            className={optionListStyle}
            optionSelectedStyle={optionSelectedStyle}
            optionHoverStyle={optionHoverStyle}
            getData={scrollerData}
            getSubscriber={getScrollerSubscriber}
            filterSignal={filterSignal || _filterSignal}
            filterLocation={filterLocation}
            dataLoadInspector={onPageDataLoaded}
            dataLoadIndicator={dataLoadIndicator}
            scrollerStyles={{ height: '200px' }}
            pageSize={pageSize}
            onNewItemCreated={onNewItemCreated}
          />
        )}
      </OptionNavigator>
    </div>
  );
};

export default ListBox;
